import { CONFIG } from '../../config/gameConfig';
import type { Bounds, Drawable } from '../../types';

const PETAL_COLOR = '#C4B5FD';
const PETAL_SHADOW = '#A695E5';
const FACE_COLOR = '#2A2A2A';
const EYE_WHITE = '#FFFFFF';

export class Bloomy implements Drawable {
  x = CONFIG.player.startX;
  y = CONFIG.player.standingY;
  velocityY = 0;
  jumping = false;
  ducking = false;
  alive = true;
  private animationFrame = 0;

  update(): void {
    if (this.jumping) {
      this.velocityY += CONFIG.player.gravity;
      this.y += this.velocityY;
      if (this.y >= CONFIG.player.standingY) {
        this.y = CONFIG.player.standingY;
        this.velocityY = 0;
        this.jumping = false;
      }
    }
    this.animationFrame = (this.animationFrame + 1) % 12;
  }

  jump(): void {
    if (this.jumping || !this.alive) return;
    this.velocityY = CONFIG.player.jumpVelocity;
    this.jumping = true;
    this.ducking = false;
  }

  startDuck(): void {
    if (!this.jumping && this.alive) this.ducking = true;
  }

  endDuck(): void {
    this.ducking = false;
  }

  reset(): void {
    this.x = CONFIG.player.startX;
    this.y = CONFIG.player.standingY;
    this.velocityY = 0;
    this.jumping = false;
    this.ducking = false;
    this.alive = true;
    this.animationFrame = 0;
  }

  bounds(): Bounds {
    if (this.isDuckingOnGround()) {
      return {
        x: this.x + 4,
        y: CONFIG.game.groundY - CONFIG.player.duckHeight + 4,
        width: CONFIG.player.duckWidth - 8,
        height: CONFIG.player.duckHeight - 6,
      };
    }
    return {
      x: this.x + 4,
      y: this.y + 4,
      width: CONFIG.player.width - 8,
      height: CONFIG.player.height - 8,
    };
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.isDuckingOnGround()) {
      const cx = this.x + CONFIG.player.duckWidth / 2;
      const cy = CONFIG.game.groundY - CONFIG.player.duckHeight / 2;
      this.drawBloomy(ctx, cx, cy, 1.35, 0.6);
      return;
    }

    const walking = this.alive && !this.jumping;
    const phase = Math.floor(this.animationFrame / 6);
    const bounceOffset = walking && phase === 1 ? -2 : 0;
    const cx = this.x + CONFIG.player.width / 2;
    const cy = this.y + CONFIG.player.height / 2 + bounceOffset;
    this.drawBloomy(ctx, cx, cy, 1, 1);
  }

  private isDuckingOnGround(): boolean {
    return this.ducking && !this.jumping;
  }

  private drawBloomy(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    sx: number,
    sy: number,
  ): void {
    const petalRadius = 13;
    const petalOffset = 11;

    ctx.fillStyle = PETAL_SHADOW;
    fillEllipse(ctx, cx, cy + petalOffset * sy * 0.85, petalRadius * sx * 1.4, petalRadius * sy * 0.55);

    ctx.fillStyle = PETAL_COLOR;
    fillEllipse(ctx, cx - petalOffset * sx, cy - petalOffset * sy, petalRadius * sx, petalRadius * sy);
    fillEllipse(ctx, cx + petalOffset * sx, cy - petalOffset * sy, petalRadius * sx, petalRadius * sy);
    fillEllipse(ctx, cx - petalOffset * sx, cy + petalOffset * sy, petalRadius * sx, petalRadius * sy);
    fillEllipse(ctx, cx + petalOffset * sx, cy + petalOffset * sy, petalRadius * sx, petalRadius * sy);
    fillEllipse(ctx, cx, cy, petalRadius * sx * 1.05, petalRadius * sy * 1.05);

    if (!this.alive) {
      this.drawDeadFace(ctx, cx, cy, sx, sy);
      return;
    }

    const eyeOffsetX = 6 * sx;
    const eyeOffsetY = -2 * sy;
    ctx.fillStyle = EYE_WHITE;
    fillEllipse(ctx, cx - eyeOffsetX, cy + eyeOffsetY, 4 * sx, 4.2 * sy);
    fillEllipse(ctx, cx + eyeOffsetX, cy + eyeOffsetY, 4 * sx, 4.2 * sy);

    ctx.fillStyle = FACE_COLOR;
    fillEllipse(ctx, cx - eyeOffsetX + 1.2 * sx, cy + eyeOffsetY + 0.5 * sy, 2.4 * sx, 2.6 * sy);
    fillEllipse(ctx, cx + eyeOffsetX + 1.2 * sx, cy + eyeOffsetY + 0.5 * sy, 2.4 * sx, 2.6 * sy);

    ctx.fillStyle = EYE_WHITE;
    fillEllipse(ctx, cx - eyeOffsetX + 2 * sx, cy + eyeOffsetY - 0.6 * sy, 0.8 * sx, 0.9 * sy);
    fillEllipse(ctx, cx + eyeOffsetX + 2 * sx, cy + eyeOffsetY - 0.6 * sy, 0.8 * sx, 0.9 * sy);

    ctx.strokeStyle = FACE_COLOR;
    ctx.lineWidth = Math.max(1.2, 1.5 * Math.min(sx, sy));
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.arc(cx, cy + 4 * sy, 3.6 * Math.min(sx, sy), 0.1 * Math.PI, 0.9 * Math.PI);
    ctx.stroke();
  }

  private drawDeadFace(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    sx: number,
    sy: number,
  ): void {
    ctx.strokeStyle = FACE_COLOR;
    ctx.lineWidth = 1.6;
    ctx.lineCap = 'round';
    const r = 2.4;
    const eyeOffsetX = 6 * sx;
    const eyeOffsetY = -2 * sy;

    [-1, 1].forEach((dir) => {
      const ex = cx + dir * eyeOffsetX;
      ctx.beginPath();
      ctx.moveTo(ex - r * sx, cy + eyeOffsetY - r * sy);
      ctx.lineTo(ex + r * sx, cy + eyeOffsetY + r * sy);
      ctx.moveTo(ex + r * sx, cy + eyeOffsetY - r * sy);
      ctx.lineTo(ex - r * sx, cy + eyeOffsetY + r * sy);
      ctx.stroke();
    });

    ctx.beginPath();
    ctx.arc(cx, cy + 5 * sy, 3 * Math.min(sx, sy), Math.PI, 2 * Math.PI);
    ctx.stroke();
  }
}

function fillEllipse(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
): void {
  ctx.beginPath();
  ctx.ellipse(cx, cy, Math.abs(rx), Math.abs(ry), 0, 0, Math.PI * 2);
  ctx.fill();
}
